import { observer } from "mobx-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/ui/panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JobRolePreference } from "./job-role-preference";
import { StartOfWeekPreference } from "./start-of-week-preference";
import type { MyJobRole } from "./use-my-job-role";
import { useStore } from "@/stores/store-context";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";

type Locale = "en" | "fr";

export const PreferencesForm = observer(function PreferencesForm({
  myJobRole,
}: {
  myJobRole: MyJobRole;
}) {
  const { t, locale, setLocale } = useTranslation();
  const { user } = useStore();
  const { theme, setTheme } = useTheme();

  const handleThemeChange = (next: string) => {
    setTheme(next);
  };

  const handleLocaleChange = async (next: Locale) => {
    setLocale(next);
    try {
      await user.updateProfile({ language: next });
    } catch (error) {
      console.error("[profile] preference save failed", requestErrorSummary(error));
      toast.error(t("profile_settings.preferences.save_error"));
    }
  };

  return (
    <Panel className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="preferences-theme">{t("profile_settings.preferences.theme_label")}</Label>
        <Select value={theme ?? "system"} onValueChange={(v) => v && handleThemeChange(v)}>
          <SelectTrigger id="preferences-theme" className="w-full">
            <SelectValue>
              {(value) =>
                value === "dark"
                  ? t("profile_settings.preferences.theme_dark")
                  : value === "light"
                    ? t("profile_settings.preferences.theme_light")
                    : t("profile_settings.preferences.theme_system")
              }
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">{t("profile_settings.preferences.theme_light")}</SelectItem>
            <SelectItem value="dark">{t("profile_settings.preferences.theme_dark")}</SelectItem>
            <SelectItem value="system">{t("profile_settings.preferences.theme_system")}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {t("profile_settings.preferences.theme_hint")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="preferences-language">
          {t("profile_settings.preferences.language_label")}
        </Label>
        <Select value={locale} onValueChange={(v) => v && void handleLocaleChange(v as Locale)}>
          <SelectTrigger id="preferences-language" className="w-full">
            <SelectValue>{(value) => (value ? t(`locale.${value}`) : null)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">{t("locale.en")}</SelectItem>
            <SelectItem value="fr">{t("locale.fr")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StartOfWeekPreference />

      <JobRolePreference myJobRole={myJobRole} />
    </Panel>
  );
});
